import {assign, forEach, isArray, isNumber, isObject, isString, map, size, some, sortBy} from 'lodash';

import {countsToArray} from 'helpers/util';

/**
 * Convert a map of gene symbols to lookup key values into a map of gene symbols to arrays or string values. In this
 * case, a lookup key can either be a string or a map of allele name to count. The map of allele name counts is
 * converted to an array containing the count of each allele specified in the key.
 *
 * This will optionally convert raw string values into either single element arrays or keep them as strings.
 *
 * This function is most useful for converting a lookupKey map into values that can be loaded into form values
 *
 * @param {object} diplotypes - a map of gene symbol strings to lookup key strings or allele count objects
 * @param {boolean} convertStringsToArray - true to convert string values to single element arrays, false to keep as strings, defaults to true
 * @return {object} a map of gene symbol to either strings or arrays
 */
export function convertDipToFn(diplotypes, convertStringsToArray = true) {
  if (!diplotypes || size(diplotypes) === 0) {
    return {};
  }

  const payload = {};
  forEach(Object.keys(diplotypes), (gene) => {
    const alleles = diplotypes[gene];
    if (isObject(alleles)) {
      payload[gene] = countsToArray(alleles);
    } else if (isArray(alleles)) {
      payload[gene] = alleles;
    } else {
      if (convertStringsToArray) {
        payload[gene] = alleles.length > 0 ? [alleles] : [];
      } else {
        payload[gene] = alleles;
      }
    }
  });

  return payload;
}

/**
 * Takes a map of gene symbol to array of gene phenotypes and creates an array of all possible keys for the combinations
 * of those phenotype values.
 *
 * @param {object} phenoMap - a Map of gene symbol String to an array of gene phenotype Strings
 * @return {object[]} an array of possible gene phenotype values, one string value per string key
 */
export function convertPhenoMapToPossibleKeys(phenoMap) {
  if (!phenoMap || phenoMap.length === 0) {
    return [];
  }
  let result = [];
  const genes = Object.keys(phenoMap);
  for (let i = 0; i < genes.length; i += 1) {
    const gene = genes[i];
    if (result.length === 0) {
      forEach(phenoMap[gene], (pheno) => result.push({[gene]: pheno}));
    } else {
      const newResult = [];
      forEach(phenoMap[gene], (pheno) => {
        forEach(result, (resultEntry) => {
          // purposely do not want all "No Result" options
          if (some([...Object.values(resultEntry), pheno], (rez) => rez !== 'No Result')) {
            newResult.push(assign({}, resultEntry, {[gene]: pheno}));
          }
        });
      });
      result = newResult;
    }
  }
  return sortBy(result, (r) => JSON.stringify(r));
}

function formatGenotypeValue(lookupKey) {
  if (!lookupKey) {
    return undefined;
  } else if (isString(lookupKey)) {
    return lookupKey;
  } else if (isArray(lookupKey)) {
    return lookupKey.join('/');
  } else {
    const payload = [];
    const nameKeys = Object.keys(lookupKey);
    for (let i = 0; i < nameKeys.length; i += 1) {
      const nameKey = nameKeys[i];
      const repeatCount = lookupKey[nameKey];
      if (!isNumber(repeatCount)) {
        throw new Error('Not a number');
      }
      for (let j = 0; j < lookupKey[nameKeys[i]]; j += 1) {
        payload.push(nameKey);
      }
    }
    return payload.join('/');
  }
}

/**
 * Converts a lookup key to a display value. The lookup key could be in a few different formats, so we handle as much as
 * we can. For each lookup key in the array:
 *
 * If it's a string just repeat it back.
 * If it's an array join the values with a backslash.
 * If it's an object assume the keys are strings and the values are integer counts, repeat the keys according to the
 * counts and join the results together.
 *
 * @param {string|object|Array} lookupKeyArray - the lookup key value
 */
export function formatLookupKey(lookupKeyArray) {
  if (!lookupKeyArray) {
    return undefined;
  } else if (isObject(lookupKeyArray)) {
    return map(lookupKeyArray, (v, k) => `${k}:${formatGenotypeValue(v)}`).join('; ');
  } else {
    return JSON.stringify(lookupKeyArray);
  }
}

/**
 * Standardizes how a function value should be shown given the properties in a Haplotype object
 *
 * @param {object} props
 * @param {object} props.functionTerm
 * @param {string} props.activityValue
 */
export function functionString({functionTerm, activityValue}) {
  if (!activityValue && !functionTerm) {
    return null;
  } else if ((!activityValue || activityValue === 'n/a') && functionTerm) {
    return functionTerm?.term;
  } else {
    return `${functionTerm?.term} (AS:${activityValue})`;
  }
}
